import { Controller } from "@hotwired/stimulus"

const MAX_DURATION = 240
const DRAG_THRESHOLD = 4

export function formatMinute(minute) {
  const normalized = ((minute % 1440) + 1440) % 1440
  const hours = Math.floor(normalized / 60)
  const mins = normalized % 60
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`
}

export function parseClock(value) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value || "")
  if (!match) return null
  const hours = Number(match[1])
  const mins = Number(match[2])
  if (hours > 23 || mins > 59) return null
  return hours * 60 + mins
}

export function snapToStep(minute, step) {
  return Math.round(minute / step) * step
}

export function minuteFromPointer(clientY, rect, windowStart, windowEnd) {
  const span = windowEnd - windowStart
  if (!rect || rect.height <= 0 || span <= 0) return windowStart
  const ratio = (clientY - rect.top) / rect.height
  const raw = windowStart + ratio * span
  return Math.min(windowEnd, Math.max(windowStart, raw))
}

export function minutePercent(minute, windowStart, windowEnd) {
  const span = windowEnd - windowStart
  if (span <= 0) return 0
  return ((minute - windowStart) / span) * 100
}

// Start and duration of a drag on the empty scale. Both ends sit on the step grid.
export function rangeFromDrag(anchor, current, step, maxDuration, windowStart, windowEnd) {
  const snappedAnchor = snapToStep(anchor, step)
  const snappedCurrent = snapToStep(current, step)
  let start = Math.min(snappedAnchor, snappedCurrent)
  let end = Math.max(snappedAnchor, snappedCurrent)
  if (end - start < step) {
    if (start + step <= windowEnd) end = start + step
    else start = end - step
  }
  if (end - start > maxDuration) {
    if (snappedCurrent >= snappedAnchor) end = start + maxDuration
    else start = end - maxDuration
  }
  if (start < windowStart) start = windowStart
  if (end > windowEnd) end = windowEnd
  if (end - start > maxDuration) end = start + maxDuration
  if (end - start < step) {
    if (start + step <= windowEnd) end = start + step
    else start = Math.max(windowStart, end - step)
  }
  return { start, duration: end - start }
}

export function clampMoveStart(minute, duration, windowStart, windowEnd, step) {
  const snapped = snapToStep(minute, step)
  const latest = Math.floor((windowEnd - duration) / step) * step
  const floor = Math.ceil(windowStart / step) * step
  return Math.min(Math.max(snapped, floor), Math.max(floor, latest))
}

export function draftAppointmentUrl(newUrl, { date, minute, duration, view }) {
  const url = new URL(newUrl, "http://localhost")
  url.searchParams.set("date", date)
  url.searchParams.set("start_time", formatMinute(minute))
  url.searchParams.set("duration_minutes", String(duration))
  if (view) url.searchParams.set("view", view)
  return `${url.pathname}${url.search}`
}

// The visit drawer loads inside its frame. Without that frame, open the page.
export function loadAppointmentFrame(frame, url, visit) {
  if (frame && "src" in frame) {
    frame.src = url
    return "frame"
  }
  visit(url)
  return "visit"
}

// Start of a click or tap on the empty scale: the step the pointer is in.
export function quickStart(minute, step) {
  return Math.floor(minute / step) * step
}

// Viewer wall-clock plus the specialist's offset, for a shared scale.
// shiftMinutes is psychologist offset minus viewer offset. Zero keeps the clock.
export function shiftClock(isoDate, minute, shiftMinutes) {
  const shift = Number(shiftMinutes) || 0
  if (!shift) return { date: isoDate, minute }
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate || "")
  if (!match) return { date: isoDate, minute }
  const utc = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  const shifted = new Date(utc + (minute + shift) * 60000)
  const month = String(shifted.getUTCMonth() + 1).padStart(2, "0")
  const day = String(shifted.getUTCDate()).padStart(2, "0")
  return {
    date: `${shifted.getUTCFullYear()}-${month}-${day}`,
    minute: shifted.getUTCHours() * 60 + shifted.getUTCMinutes()
  }
}

// Pointer gestures for the cabinet grid.
// Mouse: a click on empty space offers «Запись / Перерыв», a drag draws a range
// and opens the form, a drag on an appointment PATCHes a new start and puts the
// block back when the server rejects the time.
// Touch: the page scrolls as usual; a tap on empty space offers the same choice,
// a tap on an appointment opens it. Appointments are not dragged by touch.
export default class extends Controller {
  static targets = ["alert", "status", "quick", "quickLabel"]
  static values = {
    step: { type: Number, default: 15 },
    startMinute: Number,
    endMinute: Number,
    view: String,
    newUrl: String,
    createUrl: String,
    defaultDuration: { type: Number, default: 60 },
    error: String,
    breakCreated: String,
    zoneShift: { type: Number, default: 0 }
  }

  disconnect() {
    this.clearDrag()
    this.clearTap()
    this.closeQuick()
  }

  beginCreate(event) {
    if (event.button !== 0 || this.viewValue === "month") return
    if (event.target.closest("[data-schedule-grid-target='event']")) return
    if (event.target.closest(".schedule-grid__event")) return
    if (event.pointerType === "touch") {
      this.beginTap(event)
      return
    }
    const canvas = event.currentTarget
    this.drag = {
      type: "create",
      pointerId: event.pointerId,
      canvas,
      anchor: snapToStep(this.minuteAt(canvas, event.clientY), this.stepValue),
      startX: event.clientX,
      startY: event.clientY,
      moved: false
    }
    this.bind(canvas)
  }

  beginMove(event) {
    const el = event.currentTarget
    if (event.pointerType === "touch") return
    if (el.dataset.draggable !== "true" || event.button !== 0) return
    event.stopPropagation()
    const canvas = el.closest("[data-schedule-canvas]")
    const originMinute = parseClock(el.dataset.startTime)
    this.drag = {
      type: "move",
      pointerId: event.pointerId,
      el,
      canvas,
      originParent: el.parentElement,
      originNext: el.nextElementSibling,
      originTop: el.style.top,
      originHeight: el.style.height,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
      originDate: el.dataset.date,
      originMinute,
      grabOffset: canvas ? this.minuteAt(canvas, event.clientY) - (originMinute || 0) : 0
    }
    this.bind(el)
  }

  guardClick(event) {
    if (!this.suppressClick) return
    event.preventDefault()
    event.stopPropagation()
    this.suppressClick = false
  }

  bind(el) {
    this.moveListener = (pointerEvent) => this.onPointerMove(pointerEvent)
    this.upListener = (pointerEvent) => this.onPointerUp(pointerEvent)
    el.addEventListener("pointermove", this.moveListener)
    el.addEventListener("pointerup", this.upListener)
    el.addEventListener("pointercancel", this.upListener)
    try {
      el.setPointerCapture(this.drag.pointerId)
    } catch (_error) {
      // The pointer can already be gone.
    }
  }

  onPointerMove(event) {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return
    const distance = Math.hypot(event.clientX - this.drag.startX, event.clientY - this.drag.startY)
    if (!this.drag.moved && distance < DRAG_THRESHOLD) return
    this.drag.moved = true
    if (this.drag.type === "create") this.paintCreate(event)
    else this.paintMove(event)
  }

  onPointerUp(event) {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return
    const drag = this.drag
    this.clearDrag()
    if (!drag.moved) {
      if (drag.type === "create" && event.type === "pointerup") {
        this.openQuick(drag.canvas, event.clientX, event.clientY)
      }
      return
    }
    this.suppressClick = true
    if (drag.type === "create") this.openDraft(drag)
    else this.commitMove(drag)
  }

  // Touch keeps native scrolling: no pointer capture. A pointercancel means the
  // browser took the gesture for a scroll.
  beginTap(event) {
    this.clearTap()
    const canvas = event.currentTarget
    const tap = { canvas, pointerId: event.pointerId, startX: event.clientX, startY: event.clientY }
    tap.up = (upEvent) => {
      if (upEvent.pointerId !== tap.pointerId) return
      const distance = Math.hypot(upEvent.clientX - tap.startX, upEvent.clientY - tap.startY)
      this.clearTap()
      if (distance < DRAG_THRESHOLD * 3) this.openQuick(canvas, upEvent.clientX, upEvent.clientY)
    }
    tap.cancel = () => this.clearTap()
    canvas.addEventListener("pointerup", tap.up)
    canvas.addEventListener("pointercancel", tap.cancel)
    this.tap = tap
  }

  clearTap() {
    if (!this.tap) return
    this.tap.canvas.removeEventListener("pointerup", this.tap.up)
    this.tap.canvas.removeEventListener("pointercancel", this.tap.cancel)
    this.tap = null
  }

  openQuick(canvas, clientX, clientY) {
    if (!this.hasQuickTarget || !canvas) return
    const start = quickStart(this.minuteAt(canvas, clientY), this.stepValue)
    const duration = this.defaultDurationValue
    this.quickDraft = { date: canvas.dataset.date, start, duration }
    if (this.hasQuickLabelTarget) {
      this.quickLabelTarget.textContent = `${this.dayLabel(canvas.dataset.date)}, ${formatMinute(start)}–${formatMinute(start + duration)}`
    }
    const quick = this.quickTarget
    quick.hidden = false
    const host = this.element.getBoundingClientRect()
    const width = quick.offsetWidth || 240
    const height = quick.offsetHeight || 80
    const left = Math.min(Math.max(clientX - host.left + this.element.scrollLeft - width / 2, 4), this.element.scrollWidth - width - 4)
    const top = Math.max(clientY - host.top - height - 8, 4)
    quick.style.left = `${left}px`
    quick.style.top = `${top}px`
    quick.querySelector("button")?.focus({ preventScroll: true })
    this.quickKeydown = (keyEvent) => {
      if (keyEvent.key === "Escape") this.closeQuick()
    }
    this.quickOutside = (pointerEvent) => {
      if (!quick.contains(pointerEvent.target)) this.closeQuick()
    }
    document.addEventListener("keydown", this.quickKeydown)
    window.setTimeout(() => document.addEventListener("pointerdown", this.quickOutside), 0)
  }

  closeQuick() {
    if (this.hasQuickTarget) this.quickTarget.hidden = true
    if (this.quickKeydown) document.removeEventListener("keydown", this.quickKeydown)
    if (this.quickOutside) document.removeEventListener("pointerdown", this.quickOutside)
    this.quickKeydown = null
    this.quickOutside = null
  }

  quickSession() {
    const draft = this.quickDraft
    this.closeQuick()
    if (!draft) return
    const sent = shiftClock(draft.date, draft.start, this.zoneShiftValue)
    this.openAppointmentDraft(sent.date, sent.minute, draft.duration)
  }

  async quickBreak() {
    const draft = this.quickDraft
    this.closeQuick()
    if (!draft || !this.createUrlValue) return
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const sent = shiftClock(draft.date, draft.start, this.zoneShiftValue)
    const body = new URLSearchParams()
    body.set("scheduling_appointment[kind]", "break")
    body.set("scheduling_appointment[on_date]", sent.date)
    body.set("scheduling_appointment[start_time]", formatMinute(sent.minute))
    body.set("scheduling_appointment[duration_minutes]", String(draft.duration))
    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": token || "",
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8"
        },
        body
      })
      if (response.ok) {
        this.announce(this.breakCreatedValue)
        this.visit(window.location.href)
        return
      }
      const payload = await response.json().catch(() => ({}))
      this.showError(payload.error || this.errorValue)
    } catch (_error) {
      this.showError(this.errorValue)
    }
  }

  dayLabel(iso) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "")
    return match ? `${match[3]}.${match[2]}` : iso || ""
  }

  announce(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message || ""
  }

  paintCreate(event) {
    const canvas = this.nodeAt(event.clientX, event.clientY, "[data-schedule-canvas]") || this.drag.canvas
    if (canvas !== this.drag.canvas) {
      this.hidePreview(this.drag.canvas)
      this.drag.canvas = canvas
    }
    const current = this.minuteAt(canvas, event.clientY)
    const range = rangeFromDrag(
      this.drag.anchor,
      current,
      this.stepValue,
      MAX_DURATION,
      this.startMinuteValue,
      this.endMinuteValue
    )
    this.drag.range = range
    this.drag.date = canvas.dataset.date
    const preview = canvas.querySelector(".schedule-grid__preview")
    if (!preview) return
    preview.hidden = false
    const top = minutePercent(range.start, this.startMinuteValue, this.endMinuteValue)
    const bottom = minutePercent(range.start + range.duration, this.startMinuteValue, this.endMinuteValue)
    preview.style.top = `${top}%`
    preview.style.height = `${bottom - top}%`
    const label = preview.querySelector("[data-schedule-preview-label]")
    if (label) label.textContent = `${formatMinute(range.start)}–${formatMinute(range.start + range.duration)}`
  }

  paintMove(event) {
    const el = this.drag.el
    el.classList.add("is-dragging")
    if (this.viewValue === "month") {
      this.highlightDay(event.clientX, event.clientY)
      return
    }
    const canvas = this.nodeAt(event.clientX, event.clientY, "[data-schedule-canvas]")
    if (!canvas) return
    if (el.parentElement !== canvas) canvas.appendChild(el)
    const duration = Number(el.dataset.duration)
    const minute = clampMoveStart(
      this.minuteAt(canvas, event.clientY) - this.drag.grabOffset,
      duration,
      this.startMinuteValue,
      this.endMinuteValue,
      this.stepValue
    )
    el.style.top = `${minutePercent(minute, this.startMinuteValue, this.endMinuteValue)}%`
    this.drag.minute = minute
    this.drag.date = canvas.dataset.date
  }

  openDraft(drag) {
    this.hidePreview(drag.canvas)
    if (!drag.range || !drag.date) return
    const sent = shiftClock(drag.date, drag.range.start, this.zoneShiftValue)
    this.openAppointmentDraft(sent.date, sent.minute, drag.range.duration)
  }

  openAppointmentDraft(date, minute, duration) {
    const url = draftAppointmentUrl(this.newUrlValue, {
      date,
      minute,
      duration,
      view: this.viewValue
    })
    const frame = document.getElementById("appointment_panel")
    loadAppointmentFrame(frame, url, (target) => this.visit(target))
  }

  async commitMove(drag) {
    const el = drag.el
    const date = this.viewValue === "month" ? this.highlighted?.dataset.date : drag.date
    const minute = this.viewValue === "month" ? drag.originMinute : drag.minute
    if (!date || minute == null) {
      this.restore(drag)
      return
    }
    if (date === drag.originDate && minute === drag.originMinute) {
      this.restore(drag)
      this.suppressClick = false
      return
    }
    if (this.viewValue === "month") this.reparentMonth(el, date)
    const sent = shiftClock(date, minute, this.zoneShiftValue)
    const result = await this.patch(el.dataset.updateUrl, sent.date, formatMinute(sent.minute))
    el.classList.remove("is-dragging")
    this.clearHighlight()
    if (!result) {
      this.restore(drag)
      return
    }
    el.dataset.date = date
    el.dataset.startTime = formatMinute(minute)
    const label = el.querySelector("[data-schedule-time]")
    if (label) {
      const duration = Number(el.dataset.duration)
      label.textContent = `${formatMinute(minute)}–${formatMinute(minute + duration)}`
    }
    this.clearAlert()
    this.announce(result.message || "")
  }

  reparentMonth(el, date) {
    const day = this.element.querySelector(`[data-schedule-day][data-date="${CSS.escape(date)}"]`)
    const list = day?.querySelector("[data-schedule-events]")
    if (list && el.parentElement !== list) list.appendChild(el)
  }

  async patch(url, date, startTime) {
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const body = new URLSearchParams()
    body.set("scheduling_appointment[on_date]", date)
    body.set("scheduling_appointment[start_time]", startTime)
    body.set("scheduling_appointment[move]", "1")
    try {
      const response = await fetch(url, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": token || "",
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8"
        },
        body
      })
      const payload = await response.json().catch(() => ({}))
      if (response.ok) return payload
      this.showError(payload.error || this.errorValue)
      return null
    } catch (_error) {
      this.showError(this.errorValue)
      return null
    }
  }

  restore(drag) {
    const el = drag.el
    if (!el) return
    el.classList.remove("is-dragging")
    if (drag.originParent) {
      if (drag.originNext && drag.originNext.parentElement === drag.originParent) {
        drag.originParent.insertBefore(el, drag.originNext)
      } else {
        drag.originParent.appendChild(el)
      }
    }
    if (drag.originTop != null) el.style.top = drag.originTop
    if (drag.originHeight != null) el.style.height = drag.originHeight
    this.clearHighlight()
  }

  minuteAt(canvas, clientY) {
    return minuteFromPointer(clientY, canvas.getBoundingClientRect(), this.startMinuteValue, this.endMinuteValue)
  }

  // Columns are found from the pointer coordinates. The appointment under the
  // cursor would otherwise hide the column it is being dragged across.
  nodeAt(x, y, selector) {
    const nodes = this.element.querySelectorAll(selector)
    for (const node of nodes) {
      const rect = node.getBoundingClientRect()
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) return node
    }
    return null
  }

  highlightDay(x, y) {
    const day = this.nodeAt(x, y, "[data-schedule-day]")
    if (this.highlighted === day) return
    this.clearHighlight()
    if (!day) return
    day.classList.add("is-drop-target")
    this.highlighted = day
  }

  clearHighlight() {
    this.highlighted?.classList.remove("is-drop-target")
    this.highlighted = null
  }

  hidePreview(canvas) {
    const preview = canvas?.querySelector(".schedule-grid__preview")
    if (preview) preview.hidden = true
  }

  clearDrag() {
    const host = this.drag?.el || this.drag?.canvas
    if (host && this.moveListener) {
      host.removeEventListener("pointermove", this.moveListener)
      host.removeEventListener("pointerup", this.upListener)
      host.removeEventListener("pointercancel", this.upListener)
    }
    if (this.drag?.canvas) this.hidePreview(this.drag.canvas)
    this.drag = null
    this.moveListener = null
    this.upListener = null
  }

  visit(url) {
    if (window.Turbo) window.Turbo.visit(url, { action: url === window.location.href ? "replace" : "advance" })
    else window.location.assign(url)
  }

  showError(message) {
    if (this.hasAlertTarget) {
      this.alertTarget.hidden = !message
      this.alertTarget.textContent = message || ""
    }
    if (this.hasStatusTarget) this.statusTarget.textContent = message || ""
  }

  clearAlert() {
    this.showError("")
  }
}
